import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, AlertTriangle, Ban, Undo2 } from "lucide-react";
import { RetireDialog } from "@/components/RecordEditor";
import { ModuleNav } from "./ModuleNav";

const EMPTY = {
  analyte: "", program: "CAP", survey: "", department: "Histology", survey_year: new Date().getFullYear(),
  shipment: "", due_date: "", result_submitted_date: "", score: "", grading: "", status: "Pending",
  performed_by: "", reviewed_by: "", corrective_action_required: false, notes: "",
};

const STATUS_STYLE = {
  Pass: "bg-[#134e4a] text-white",
  Unsatisfactory: "bg-red-800 text-white",
  Unacceptable: "bg-red-800 text-white",
  Pending: "bg-amber-100 text-amber-900",
  "Not graded": "bg-slate-200 text-slate-700",
};

export default function ProficiencyTesting() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", department: "", status: "", flag: "", voided: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [detail, setDetail] = useState(null);
  const [retireEvent, setRetireEvent] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const p = { ...filters };
      Object.keys(p).forEach((k) => !p[k] && delete p[k]);
      const { data } = await api.get("/pt-events", { params: p });
      setRows(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => { api.get("/accreditation/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const canEdit = user?.role !== "viewer";

  const clean = (body) => {
    const out = { ...body };
    ["due_date", "result_submitted_date"].forEach((k) => { if (!out[k]) out[k] = null; });
    out.survey_year = out.survey_year === "" ? null : Number(out.survey_year);
    return out;
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy("create");
    try {
      await api.post("/pt-events", clean(form));
      toast.success(`${form.analyte} logged`);
      setOpen(false);
      setForm({ ...EMPTY });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy("save");
    try {
      const { data } = await api.put(`/pt-events/${detail.id}`, clean({
        due_date: (detail.due_date || "").slice(0, 10),
        result_submitted_date: (detail.result_submitted_date || "").slice(0, 10),
        score: detail.score || "", grading: detail.grading || "", status: detail.status,
        performed_by: detail.performed_by || "", reviewed_by: detail.reviewed_by || "",
        corrective_action_required: !!detail.corrective_action_required, notes: detail.notes || "",
        survey_year: detail.survey_year ?? "",
      }));
      setDetail(null);
      toast.success(data.failed ? "Result recorded — corrective action required" : "Result recorded");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="pt-page">
      <ModuleNav
        title="Proficiency Testing Tracker"
        blurb="Every survey the lab is enrolled in, by analyte — when it is due, what was submitted, how it graded and whether a corrective action is owed."
        actions={canEdit && (
          <Button onClick={() => setOpen(true)} data-testid="new-pt-button"
                  className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
            <Plus size={16} className="mr-2" /> Log PT event
          </Button>
        )}
      />

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-slate-50 p-3">
        <div className="min-w-[200px] flex-1">
          <Label className="text-xs uppercase tracking-wide text-slate-600">Search</Label>
          <div className="relative mt-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                   data-testid="pt-search-input" placeholder="Analyte, program or shipment"
                   className="h-11 border-slate-300 pl-9" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
          <select value={filters.department} onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))}
                  data-testid="pt-department-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All departments</option>
            {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Status</Label>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  data-testid="pt-status-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">Any status</option>
            {(meta?.pt_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Flag</Label>
          <select value={filters.flag} onChange={(e) => setFilters((f) => ({ ...f, flag: e.target.value }))}
                  data-testid="pt-flag-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">No flag</option>
            <option value="failed">Failed</option>
            <option value="overdue">Overdue</option>
            <option value="pending">Awaiting result</option>
            <option value="ca">Corrective action owed</option>
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">View</Label>
          <select value={filters.voided} onChange={(e) => setFilters((f) => ({ ...f, voided: e.target.value }))}
                  data-testid="pt-voided-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">Active events</option>
            <option value="true">Retired events</option>
          </select>
        </div>
        <Button variant="outline" data-testid="pt-clear-filters" className="h-11 border-slate-300"
                onClick={() => setFilters({ q: "", department: "", status: "", flag: "", voided: "" })}>Clear</Button>
      </div>

      <div className="border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-sm" data-testid="pt-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2">Analyte</th><th className="px-3 py-2">Program / survey</th>
                <th className="px-3 py-2">Department</th><th className="px-3 py-2">Due</th>
                <th className="px-3 py-2">Submitted</th><th className="px-3 py-2">Score</th>
                <th className="px-3 py-2">Status</th><th className="px-3 py-2">Performed / reviewed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`pt-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => setDetail({ ...r })} data-testid={`pt-open-${r.id}`}
                            className="font-semibold text-[#134e4a] hover:underline">{r.analyte}</button>
                    {r.corrective_action_required && (
                      <span className="ml-2 inline-flex items-center gap-1 bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900"
                            data-testid={`pt-ca-flag-${r.id}`}>
                        <AlertTriangle size={11} /> CA owed
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {r.program}{r.survey ? ` · ${r.survey}` : ""}
                    <div className="text-[11px] text-slate-500">{r.survey_year || ""}{r.shipment ? ` shipment ${r.shipment}` : ""}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.department}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.due_date ? (
                      <span className={r.overdue ? "font-semibold text-red-800" : r.at_risk ? "font-semibold text-amber-800" : "text-slate-600"}>
                        {fmtDate(r.due_date)}{r.overdue ? ` · ${Math.abs(r.days_until_due)}d overdue` : ""}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.result_submitted_date ? fmtDate(r.result_submitted_date) : "—"}</td>
                  <td className="px-3 py-2 text-slate-700">{r.score || "—"}{r.grading ? ` (${r.grading})` : ""}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.performed_by || "—"}{r.reviewed_by ? ` / ${r.reviewed_by}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="px-3 py-10 text-center" data-testid="pt-empty">
            <p className="text-sm text-slate-500">
              {Object.values(filters).some(Boolean) ? "No PT events match these filters." : "No proficiency testing events logged yet."}
            </p>
            {canEdit && (
              <Button onClick={() => setOpen(true)} data-testid="empty-add-pt"
                      className="mt-4 h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                <Plus size={16} className="mr-2" /> Log your first PT event
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="new-pt-dialog">
          <DialogHeader>
            <DialogTitle>Log proficiency testing event</DialogTitle>
            <DialogDescription>Enrol the survey now; record the result and grading when it comes back.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Analyte / test</Label>
                <Input value={form.analyte} required minLength={2} placeholder="e.g. HER2 by IHC"
                       onChange={(e) => setForm((f) => ({ ...f, analyte: e.target.value }))}
                       data-testid="pt-analyte-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Program</Label>
                <Input value={form.program} placeholder="CAP"
                       onChange={(e) => setForm((f) => ({ ...f, program: e.target.value }))}
                       data-testid="pt-program-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Survey</Label>
                <Input value={form.survey} onChange={(e) => setForm((f) => ({ ...f, survey: e.target.value }))}
                       data-testid="pt-survey-input" placeholder="HER2-B" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Year</Label>
                <Input type="number" value={form.survey_year}
                       onChange={(e) => setForm((f) => ({ ...f, survey_year: e.target.value }))}
                       data-testid="pt-year-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Shipment</Label>
                <Input value={form.shipment} onChange={(e) => setForm((f) => ({ ...f, shipment: e.target.value }))}
                       data-testid="pt-shipment-input" placeholder="A" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
                <select value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                        data-testid="pt-department-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Due date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                       data-testid="pt-due-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Result submitted</Label>
                <Input type="date" value={form.result_submitted_date}
                       onChange={(e) => setForm((f) => ({ ...f, result_submitted_date: e.target.value }))}
                       data-testid="pt-submitted-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Status</Label>
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                        data-testid="pt-status-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.pt_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Score</Label>
                <Input value={form.score} onChange={(e) => setForm((f) => ({ ...f, score: e.target.value }))}
                       data-testid="pt-score-input" placeholder="5/5" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Grading</Label>
                <Input value={form.grading} onChange={(e) => setForm((f) => ({ ...f, grading: e.target.value }))}
                       data-testid="pt-grading-input" placeholder="100%" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Performed by</Label>
                <Input value={form.performed_by} onChange={(e) => setForm((f) => ({ ...f, performed_by: e.target.value }))}
                       data-testid="pt-performed-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Reviewed by</Label>
                <Input value={form.reviewed_by} onChange={(e) => setForm((f) => ({ ...f, reviewed_by: e.target.value }))}
                       data-testid="pt-reviewed-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.corrective_action_required}
                     onChange={(e) => setForm((f) => ({ ...f, corrective_action_required: e.target.checked }))}
                     data-testid="pt-ca-input" className="h-4 w-4 accent-[#134e4a]" />
              Corrective action required
            </label>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                        data-testid="pt-notes-input" className="mt-1 min-h-[60px] border-slate-300" />
            </div>
            <Button type="submit" disabled={busy === "create"} data-testid="create-pt-submit"
                    className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy === "create" ? "Saving…" : "Log event"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="pt-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.analyte}</DialogTitle>
            <DialogDescription>
              {detail?.program} {detail?.survey} {detail?.survey_year || ""} · {detail?.department}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <form onSubmit={save} className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Due date</Label>
                  <Input type="date" value={(detail.due_date || "").slice(0, 10)} disabled={!canEdit}
                         onChange={(e) => setDetail((d) => ({ ...d, due_date: e.target.value }))}
                         data-testid="pt-detail-due" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Result submitted</Label>
                  <Input type="date" value={(detail.result_submitted_date || "").slice(0, 10)} disabled={!canEdit}
                         onChange={(e) => setDetail((d) => ({ ...d, result_submitted_date: e.target.value }))}
                         data-testid="pt-detail-submitted" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Status</Label>
                  <select value={detail.status} disabled={!canEdit}
                          onChange={(e) => setDetail((d) => ({ ...d, status: e.target.value }))}
                          data-testid="pt-detail-status" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                    {(meta?.pt_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-4">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Score</Label>
                  <Input value={detail.score || ""} disabled={!canEdit}
                         onChange={(e) => setDetail((d) => ({ ...d, score: e.target.value }))}
                         data-testid="pt-detail-score" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Grading</Label>
                  <Input value={detail.grading || ""} disabled={!canEdit}
                         onChange={(e) => setDetail((d) => ({ ...d, grading: e.target.value }))}
                         data-testid="pt-detail-grading" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Performed by</Label>
                  <Input value={detail.performed_by || ""} disabled={!canEdit}
                         onChange={(e) => setDetail((d) => ({ ...d, performed_by: e.target.value }))}
                         data-testid="pt-detail-performed" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Reviewed by</Label>
                  <Input value={detail.reviewed_by || ""} disabled={!canEdit}
                         onChange={(e) => setDetail((d) => ({ ...d, reviewed_by: e.target.value }))}
                         data-testid="pt-detail-reviewed" className="mt-1 h-11 border-slate-300" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!detail.corrective_action_required} disabled={!canEdit}
                       onChange={(e) => setDetail((d) => ({ ...d, corrective_action_required: e.target.checked }))}
                       data-testid="pt-detail-ca" className="h-4 w-4 accent-[#134e4a]" />
                Corrective action required — log it in the nonconformance register
              </label>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Notes</Label>
                <Textarea value={detail.notes || ""} disabled={!canEdit}
                          onChange={(e) => setDetail((d) => ({ ...d, notes: e.target.value }))}
                          data-testid="pt-detail-notes" className="mt-1 min-h-[70px] border-slate-300" />
              </div>
              {canEdit && (
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" disabled={busy === "save"} data-testid="pt-detail-save"
                          className="h-11 flex-1 bg-[#134e4a] hover:bg-[#0f3f3c]">
                    {busy === "save" ? "Saving…" : "Save result"}
                  </Button>
                  {detail.voided ? (
                    <Button type="button" variant="outline" data-testid="pt-restore-button" className="h-11 border-slate-300"
                            onClick={async () => {
                              try {
                                await api.post(`/pt-events/${detail.id}/restore`);
                                toast.success("PT event reinstated");
                                setDetail(null);
                                load();
                              } catch (e) { toast.error(apiError(e)); }
                            }}>
                      <Undo2 size={15} className="mr-2" /> Reinstate
                    </Button>
                  ) : (
                    <Button type="button" variant="outline" onClick={() => setRetireEvent(detail)}
                            data-testid="pt-retire-button" className="h-11 border-red-300 text-red-800 hover:bg-red-50">
                      <Ban size={15} className="mr-2" /> Retire
                    </Button>
                  )}
                </div>
              )}
            </form>
          )}
        </DialogContent>
      </Dialog>
      <RetireDialog
        open={!!retireEvent} onOpenChange={(v) => !v && setRetireEvent(null)} testid="retire-pt-dialog"
        title={`Retire ${retireEvent?.analyte || ""}`} confirmLabel="Retire PT event"
        description="Use this when an event was logged in error or duplicated. It stays on file under the Retired view."
        onConfirm={async (reason) => {
          await api.post(`/pt-events/${retireEvent.id}/void`, { reason });
          toast.success("PT event retired");
          setDetail(null);
          load();
        }}
      />
    </div>
  );
}
